// Encrypted device storage of small secrets, WebCrypto only so this module
// runs unchanged in Node 22 and in browsers. PBKDF2-HMAC-SHA-256 stretches
// the passphrase into an AES-256-GCM key; the additional authenticated data
// binds the ciphertext to one envelope purpose, so a sealed master can never
// be opened as a sealed relayer seed or vice versa, and neither can be
// replayed as some other kind of sealed blob. The master (k, c) envelope is
// the original format ('THEVAULT/master/1'); the relayer envelope
// (docs/08-app-design.md 5.7, the gas tank's burner Ed25519 seed) reuses the
// same construction under its own purpose string. With the fee reserve
// derived from the master (docs/18-one-fee-reserve.md section 9), the relayer
// envelope is the LEGACY per-device envelope: the purpose stays, with no
// format change, so the envelopes legacy devices hold still open.

const PURPOSE_AAD = {
  master: 'THEVAULT/master/1',
  // LEGACY: the per-device relayer seed envelope (docs/18 section 9); kept only to open existing envelopes.
  relayer: 'THEVAULT/relayer/1',
  jupiter: 'THEVAULT/jupiter/1',
} as const;
export type EnvelopePurpose = keyof typeof PURPOSE_AAD;

const SALT_LEN = 16;
const IV_LEN = 12;
const MASTER_LEN = 64; // k (32) || c (32)
/** The LEGACY relayer envelope's plaintext, a 32-byte Ed25519 seed. */
export const RELAYER_SEED_LEN = 32;

export interface SealedMaster {
  v: 1;
  kdf: 'PBKDF2-SHA256';
  iterations: number;
  salt: string; // hex, 16 bytes
  iv: string; // hex, 12 bytes
  ciphertext: string; // hex
}

/** Same envelope shape for every purpose; the purpose is bound through the AAD, not the JSON. */
export type SealedEnvelope = SealedMaster;

export type MasterStorageErrorCode = 'BadPassphraseOrTampered' | 'Unsupported';

export class MasterStorageError extends Error {
  constructor(readonly code: MasterStorageErrorCode, message: string) {
    super(message);
    this.name = 'MasterStorageError';
  }
}

function toHex(b: Uint8Array): string {
  return Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
}

// Uint8Array<ArrayBuffer> (not the bare, ArrayBufferLike-default Uint8Array) so
// these values satisfy WebCrypto's BufferSource parameters without a cast.
function fromHex(s: string): Uint8Array<ArrayBuffer> {
  return Uint8Array.from(s.match(/../g) ?? [], (x) => parseInt(x, 16));
}

function randomBytes(n: number): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(n);
  globalThis.crypto.getRandomValues(out);
  return out;
}

async function deriveKey(passphrase: string, salt: Uint8Array<ArrayBuffer>, iterations: number): Promise<CryptoKey> {
  const material = await globalThis.crypto.subtle.importKey('raw', new TextEncoder().encode(passphrase.normalize('NFKD')), 'PBKDF2', false, ['deriveKey']);
  return globalThis.crypto.subtle.deriveKey({ name: 'PBKDF2', salt, iterations, hash: 'SHA-256' }, material, { name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
}

/**
 * Encrypt `plaintext` under a passphrase-derived AES-256-GCM key with a fresh random salt and IV, bound to `purpose`.
 * The caller owns and wipes `plaintext`; the copy handed to WebCrypto is this function's own and is zeroed in `finally`,
 * so a rejected encryption leaves no plaintext of ours behind (R91).
 */
export async function sealBytes(plaintext: Uint8Array, passphrase: string, purpose: EnvelopePurpose, iterations = 600_000): Promise<SealedEnvelope> {
  const salt = randomBytes(SALT_LEN);
  const iv = randomBytes(IV_LEN);
  const key = await deriveKey(passphrase, salt, iterations);
  const copy = new Uint8Array(plaintext.length);
  copy.set(plaintext);
  try {
    const ciphertext = await globalThis.crypto.subtle.encrypt({ name: 'AES-GCM', iv, additionalData: new TextEncoder().encode(PURPOSE_AAD[purpose]) }, key, copy);
    return { v: 1, kdf: 'PBKDF2-SHA256', iterations, salt: toHex(salt), iv: toHex(iv), ciphertext: toHex(new Uint8Array(ciphertext)) };
  } finally {
    copy.fill(0);
  }
}

/**
 * Reverse of sealBytes for the same `purpose`. Throws MasterStorageError('BadPassphraseOrTampered')
 * on a wrong passphrase, a tampered ciphertext or an envelope of another purpose (all three are
 * AES-GCM authentication failures), and ('Unsupported') on an envelope whose version or KDF this
 * code does not implement. `expectedLength`, when given, is checked against the plaintext: an envelope that
 * authenticates but holds another length is refused, and its decrypted bytes are zeroed before the refusal (R91).
 * On a valid return the caller owns the buffer and zeroes it.
 */
export async function openBytes(sealed: SealedEnvelope, passphrase: string, purpose: EnvelopePurpose, expectedLength?: number): Promise<Uint8Array> {
  if (sealed.v !== 1 || sealed.kdf !== 'PBKDF2-SHA256') throw new MasterStorageError('Unsupported', `unsupported envelope v=${sealed.v} kdf=${sealed.kdf}`);
  const key = await deriveKey(passphrase, fromHex(sealed.salt), sealed.iterations);
  let plaintext: ArrayBuffer;
  try {
    plaintext = await globalThis.crypto.subtle.decrypt({ name: 'AES-GCM', iv: fromHex(sealed.iv), additionalData: new TextEncoder().encode(PURPOSE_AAD[purpose]) }, key, fromHex(sealed.ciphertext));
  } catch {
    throw new MasterStorageError('BadPassphraseOrTampered', 'decryption failed: wrong passphrase, tampered ciphertext or an envelope of another purpose');
  }
  const bytes = new Uint8Array(plaintext);
  if (expectedLength !== undefined && bytes.length !== expectedLength) {
    bytes.fill(0);
    throw new MasterStorageError('BadPassphraseOrTampered', `decrypted envelope has the wrong length ${bytes.length}`);
  }
  return bytes;
}

/** Encrypt k || c under a passphrase-derived AES-256-GCM key with a fresh random salt and IV. */
export async function sealMaster(master: { k: Uint8Array; c: Uint8Array }, passphrase: string, iterations = 600_000): Promise<SealedMaster> {
  if (master.k.length !== 32 || master.c.length !== 32) throw new RangeError('master k and c must each be 32 bytes');
  const plaintext = new Uint8Array(MASTER_LEN);
  plaintext.set(master.k, 0);
  plaintext.set(master.c, 32);
  try {
    return await sealBytes(plaintext, passphrase, 'master', iterations);
  } finally {
    plaintext.fill(0);
  }
}

/**
 * Reverse of sealMaster. Throws MasterStorageError('BadPassphraseOrTampered') on a wrong
 * passphrase or a tampered ciphertext (AES-GCM authentication failure), and
 * ('Unsupported') on an envelope whose version or KDF this code does not implement.
 */
export async function openMaster(sealed: SealedMaster, passphrase: string): Promise<{ k: Uint8Array; c: Uint8Array }> {
  const bytes = await openBytes(sealed, passphrase, 'master', MASTER_LEN);
  const out = { k: bytes.slice(0, 32), c: bytes.slice(32, 64) };
  bytes.fill(0);
  return out;
}

/** LEGACY (docs/18 section 9): the gas tank's per-device burner seed (docs/08 5.7), sealed under the device passphrase like the master but under its own purpose. */
export async function sealRelayerSeed(seed: Uint8Array, passphrase: string, iterations = 600_000): Promise<SealedEnvelope> {
  if (seed.length !== RELAYER_SEED_LEN) throw new RangeError(`relayer seed must be ${RELAYER_SEED_LEN} bytes`);
  return sealBytes(seed, passphrase, 'relayer', iterations);
}

/** Opens a LEGACY per-device relayer envelope (docs/18 section 9; the sweep's `openLegacy`, docs/18 section 6). */
export async function openRelayerSeed(sealed: SealedEnvelope, passphrase: string): Promise<Uint8Array> {
  return openBytes(sealed, passphrase, 'relayer', RELAYER_SEED_LEN);
}

/** The Jupiter API key (docs/08-app-design.md 5.11), sealed under the device passphrase like the master and the relayer seed but under its own purpose: UTF-8 bytes through sealBytes/openBytes, so a jupiter envelope can never be opened as a master or a relayer seed. */
export async function sealJupiterKey(key: string, passphrase: string, iterations = 600_000): Promise<SealedEnvelope> {
  const plaintext = new TextEncoder().encode(key);
  try {
    return await sealBytes(plaintext, passphrase, 'jupiter', iterations);
  } finally {
    plaintext.fill(0);
  }
}

export async function openJupiterKey(sealed: SealedEnvelope, passphrase: string): Promise<string> {
  const bytes = await openBytes(sealed, passphrase, 'jupiter');
  try {
    return new TextDecoder().decode(bytes);
  } finally {
    // The caller owns the decrypted buffer (openBytes); the string it decodes to cannot be zeroed, the bytes can.
    bytes.fill(0);
  }
}
